import { hasCSSSelectorSupport } from "@otto-ec/otto-components-utils/utils/canIUse";
import { mount, unmount, type Component } from "svelte";
import { extendElementWithActionAttributes } from "../customElement/extendElementWithActionAttributes.ts";
import { useShadowStylesInjector } from "./utils/useShadowStylesInjector.ts";
import { hostContextKey } from "./utils/useHost.ts";
import { createObservedAttributes } from "./utils/createObservedAttributes.ts";
import type { DefineComponentOptions, DefineComponentProps } from "./types.ts";
import { setMounted } from "./utils/setMounted.ts";
import { resolveComponent } from "./utils/resolveComponent.ts";
import { parseAttributeToValue, stringifyValueToAttribute } from "./utils/parseAttributeToValue.ts";
import { ocProps } from "./utils/ocProps.ts";
import { shouldAttachShadow } from "../customElement/extend.base.ts";

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function defineComponent<Props extends Record<string, unknown>, Methods extends Record<string, unknown> = {}>({
  tag,
  styles,
  props,
  html,
  extendOptions = {},
  onMount,
  component,
}: DefineComponentOptions<Props, Methods>): void {
  const injectShadowStyles = useShadowStylesInjector(styles);

  const propsDefinitions = (props ?? {}) as DefineComponentProps<Record<string, unknown>>;
  const attributeMetaMap = Object.fromEntries(
    Object.entries(propsDefinitions).map(
      ([propName, { type, attribute = propName }]) =>
        [attribute as string, { propName: propName as string, type }] as const,
    ),
  );

  let ElementClass = class extends HTMLElement implements Record<string, unknown> {
    static observedAttributes = createObservedAttributes(propsDefinitions);

    mounted: boolean = false;
    #ocEffectRootUnset?: () => void;
    /*                                                                                                     */
    #ocDisconnectToken?: symbol;
    /*                                                                                                                                                */
    #ocPendingTeardown?: Promise<void>;
    /*                                                                                                              */
    /*                                                       */
    $ocReflecting: Set<string> = new Set<string>();
    $ocProps: Record<string, unknown> | undefined;
    $ocSvelteComponent?: Record<string, unknown>;
    $onDestroyCallbacks: ((() => void) | void | undefined)[] = [];
    [x: string]: unknown;

    attributeChangedCallback(name: string, _: string | null, newValue: string | null): void {
      if (this.$ocReflecting.has(name)) return;
      const { propName, type } = attributeMetaMap[name];
      const value = parseAttributeToValue(newValue, type);

      if (ocProps(this)[propName] === value) return;

      ocProps(this)[propName] = value;
      if (this.$ocSvelteComponent) this.$ocSvelteComponent[propName] = value;
    }

    async connectedCallback(): Promise<void> {
      const pendingTeardown = this.#ocPendingTeardown;
      if (this.#ocDisconnectToken) {
        /*                                                                                                                */
        this.#ocDisconnectToken = undefined;
        if (!pendingTeardown) return; /*                                                                         */
      }
      /*                                                                                                                  */
      if (pendingTeardown) await pendingTeardown;

      /*                                                        */
      Object.entries(propsDefinitions).forEach(
        ([propName, { default: defaultValue, reflect, attribute = propName, type }]) => {
          if (Object.hasOwn(ocProps(this), propName)) return;
          if (Object.hasOwn(this, propName)) {
            /*                                                                                                                           */
            ocProps(this)[propName] = this[propName];
            delete this[propName];
          } else if (defaultValue !== undefined) {
            ocProps(this)[propName] = defaultValue;
          }

          /*                                                                                                            */
          if (reflect && ocProps(this)[propName] !== undefined) {
            this.$ocReflecting.add(attribute);
            stringifyValueToAttribute(this, attribute, ocProps(this)[propName], type);
            this.$ocReflecting.delete(attribute);
          }
        },
      );

      if (html) {
        if (shouldAttachShadow(this, extendOptions)) {
          this.attachShadow({ mode: "open" });
        }

        (this.shadowRoot ?? this).innerHTML = html;
      } else {
        /*                                                                              */
        const resolvedComponentOrPromise = component ? resolveComponent(component) : undefined;
        const resolvedComponent =
          Promise.resolve(resolvedComponentOrPromise) === resolvedComponentOrPromise
            ? await resolvedComponentOrPromise
            : (resolvedComponentOrPromise as Component | undefined);

        if (resolvedComponent) {
          if (shouldAttachShadow(this, extendOptions)) {
            this.attachShadow({ mode: "open" });
          }

          this.#ocEffectRootUnset = $effect.root(() => {
            /*                                                                          */

            /*                                                                                   */
            this.$ocSvelteComponent = mount(resolvedComponent, {
              target: this.shadowRoot ? this.shadowRoot : this,
              props: ocProps(this),
              /*                                                       */
              context: new Map([[hostContextKey, this]]),
            });

            /*                                          */
            Object.assign(this, this.$ocSvelteComponent, ocProps(this));

            /*                                                              */
            /*                                                              */
            Object.keys(propsDefinitions).forEach((propName) => {
              /*                                                                                                */
              /*                                                                                                                 */
              let isFirstRun = true;
              $effect(() => {
                /*                                                                                        */
                const [value] = [this.$ocSvelteComponent![propName]] as const;

                /*                                                                      */
                /*                                                                       */
                /*                                                                                                                   */
                if (!isFirstRun || value !== undefined)
                  (this as Record<string, unknown>)[propName] = value;

                isFirstRun = false;
              });
            });
          });
        }
      }

      /*                                              */
      await injectShadowStyles(this);

      /*                                                                         */
      this.$onDestroyCallbacks.push(onMount?.(this as never, ocProps(this) as never));

      setMounted(tag, this, true);
    }

    async disconnectedCallback(): Promise<void> {
      const token = (this.#ocDisconnectToken = Symbol());
      await Promise.resolve(); /*                                                                              */
      if (this.#ocDisconnectToken !== token) return; /*                                                           */

      /*                                                                                                */
      this.#ocPendingTeardown = this.#finalizeDisconnect();
      await this.#ocPendingTeardown;
      this.#ocPendingTeardown = undefined;
    }

    async #finalizeDisconnect(): Promise<void> {
      /*                                                                                        */
      this.mounted = false;

      /*                                                                                             */
      const svelteComponent = this.$ocSvelteComponent;
      const onDestroyCallbacks = this.$onDestroyCallbacks;
      this.$ocSvelteComponent = undefined;
      this.$onDestroyCallbacks = [];

      this.#ocEffectRootUnset?.();
      if (svelteComponent) await unmount(svelteComponent);
      onDestroyCallbacks.forEach((callback) => callback?.());

      this.#ocDisconnectToken = undefined; /*                                                              */

      /*                                                                               */
      if (!this.mounted) setMounted(tag, this, false);
    }
  };

  Object.entries(propsDefinitions).forEach(
    ([propName, { type, attribute = propName, reflect }]) => {
      Object.defineProperty(ElementClass.prototype, propName, {
        get(this: InstanceType<typeof ElementClass>): unknown {
          return ocProps(this)[propName];
        },
        set(this: InstanceType<typeof ElementClass>, value: unknown) {
          if (ocProps(this)[propName] === value) return;
          ocProps(this)[propName] = value;

          if (this.$ocSvelteComponent) this.$ocSvelteComponent[propName] = value;

          /*                                                                   */
          /*                                                                   */
          /*                                                                  */
          /*                                                                   */
          /*                                                                    */
          if (reflect) {
            this.$ocReflecting.add(attribute);
            stringifyValueToAttribute(this, attribute, value, type);
            this.$ocReflecting.delete(attribute);
          }
        },
      });
    },
  );

  /*                                                                   */
  /*                                                                     */
  const extendOptionsParent = { ...extendOptions };
  if (!import.meta.env.STORYBOOK && !import.meta.env.VITE_IS_TEST_BUILD) {
    extendOptionsParent.useShadow = false;
  }

  ElementClass = window.__components.extend(extendOptionsParent)(ElementClass) as never;

  /*                                             */
  /*                                                    */
  if (!hasCSSSelectorSupport)
    ElementClass = extendElementWithActionAttributes(ElementClass as never) as never;

  customElements.define(tag, ElementClass);
}

window.__components ??= {} as never;
window.__components.defineComponent = defineComponent;
